import { supabase } from "@/lib/supabase";
import type { Tables } from "@/lib/database.types";
import {
  buildBillingPageState,
} from "@/features/billing/billingOffers";
import type {
  BillingCheckoutRequest,
  BillingCheckoutResponse,
  BillingHistoryItem,
  BillingOverview,
  BillingPageState,
  BillingPortalRequest,
  BillingPortalResponse,
  BillingSubscriptionSnapshot,
  CreditWalletSnapshot,
} from "@/features/billing/billing.types";

type SubscriptionRow = Tables<"subscriptions">;
type CreditsRow = Tables<"credits">;
type CreditTransactionRow = Tables<"credit_transactions">;

function mapSubscription(row: SubscriptionRow | null): BillingSubscriptionSnapshot {
  return {
    billingInterval: row?.billing_interval ?? null,
    cancelAtPeriodEnd: row?.cancel_at_period_end ?? false,
    currentPeriodEnd: row?.current_period_end ?? null,
    status: row?.status ?? "active",
    stripeCustomerId: row?.stripe_customer_id ?? null,
    stripeSubscriptionId: row?.stripe_subscription_id ?? null,
    tier: row?.tier ?? "free",
  };
}

function mapWallet(row: CreditsRow | null): CreditWalletSnapshot {
  return {
    allocationResetAt: row?.allocation_reset_at ?? null,
    balance: row?.balance ?? 0,
    monthlyAllocation: row?.monthly_allocation ?? 0,
  };
}

function mapHistory(rows: CreditTransactionRow[] | null): BillingHistoryItem[] {
  return (rows ?? []).map((row) => ({
    amount: row.amount,
    createdAt: row.created_at,
    feature: row.feature,
    id: row.id,
    metadata: row.metadata,
  }));
}

export async function getBillingOverview(): Promise<BillingOverview> {
  const [subscriptionResult, creditsResult, historyResult] = await Promise.all([
    supabase.from("subscriptions").select("*").maybeSingle(),
    supabase.from("credits").select("*").maybeSingle(),
    supabase
      .from("credit_transactions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(25),
  ]);

  if (subscriptionResult.error) {
    throw subscriptionResult.error;
  }

  if (creditsResult.error) {
    throw creditsResult.error;
  }

  if (historyResult.error) {
    throw historyResult.error;
  }

  return {
    history: mapHistory(historyResult.data),
    subscription: mapSubscription(subscriptionResult.data),
    wallet: mapWallet(creditsResult.data),
  };
}

export async function getBillingPageState(): Promise<BillingPageState> {
  const overview = await getBillingOverview();
  return buildBillingPageState(overview);
}

export async function createCheckoutSession(
  request: BillingCheckoutRequest,
): Promise<BillingCheckoutResponse> {
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    body: {
      mode: request.mode,
      offerCode: request.offerCode,
      returnPath: request.returnPath,
    },
  });

  if (error || !data) {
    throw error ?? new Error("Could not create checkout session.");
  }

  return data as BillingCheckoutResponse;
}

export async function createBillingPortalSession(
  request: BillingPortalRequest = {},
): Promise<BillingPortalResponse> {
  const { data, error } = await supabase.functions.invoke("billing-portal", {
    body: request,
  });

  if (error || !data) {
    throw error ?? new Error("Could not create billing portal session.");
  }

  return data as BillingPortalResponse;
}

export async function refreshCreditWallet() {
  const { data, error } = await supabase.from("credits").select("*").maybeSingle();

  if (error) {
    throw error;
  }

  return mapWallet(data);
}
